import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useBuilderStore } from '../../stores/builderStore';
import { fieldRegistry } from '../../lib/fieldRegistry';
import type { FormField, FieldOption, ConditionalRule, Condition, ConditionOperator, ConditionalAction, ValidationMessages, MatrixRow, MatrixColumn, RankingItem, ImageChoiceOption, AppointmentAvailability, OpinionScaleLabel } from '../../types/form';

export default function FieldConfigurator() {
  const { formSchema, selectedFieldId, updateField, selectField } = useBuilderStore();

  const field = formSchema.fields.find((f) => f.id === selectedFieldId);

  if (!field) {
    return (
      <div className="w-80 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <p>Select a field to configure</p>
        </div>
      </div>
    );
  }

  const currentField = field; // For use in closures
  const fieldDef = fieldRegistry[currentField.type];

  function handleChange<K extends keyof FormField>(key: K, value: FormField[K]) {
    updateField(currentField.id, { [key]: value });
  }

  function handleOptionsChange(options: FieldOption[]) {
    updateField(currentField.id, { options });
  }

  function addOption() {
    const newOption: FieldOption = {
      label: `Option ${(currentField.options?.length || 0) + 1}`,
      value: `option${(currentField.options?.length || 0) + 1}`,
    };
    handleOptionsChange([...(currentField.options || []), newOption]);
  }

  function updateOption(index: number, updates: Partial<FieldOption>) {
    const newOptions = [...(currentField.options || [])];
    newOptions[index] = { ...newOptions[index], ...updates };
    handleOptionsChange(newOptions);
  }

  function removeOption(index: number) {
    const newOptions = (currentField.options || []).filter((_, i) => i !== index);
    handleOptionsChange(newOptions);
  }

  return (
    <div className="w-80 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 rounded">
              {fieldDef.icon}
            </span>
            <span className="font-medium dark:text-white">{fieldDef.label}</span>
          </div>
          <button
            onClick={() => selectField(null)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* General Settings */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">General</h3>

        <div className="space-y-4">
          <div>
            <label className="label">Label</label>
            <input
              type="text"
              value={field.label}
              onChange={(e) => handleChange('label', e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label className="label">Field Name</label>
            <input
              type="text"
              value={field.name}
              onChange={(e) => handleChange('name', e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
              className="input font-mono text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Used in submissions data</p>
          </div>

          <div>
            <label className="label">Placeholder</label>
            <input
              type="text"
              value={field.placeholder || ''}
              onChange={(e) => handleChange('placeholder', e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label className="label">Help Text</label>
            <input
              type="text"
              value={field.helpText || ''}
              onChange={(e) => handleChange('helpText', e.target.value)}
              className="input"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="required"
              checked={field.required}
              onChange={(e) => handleChange('required', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700"
            />
            <label htmlFor="required" className="text-sm text-gray-700 dark:text-gray-300 dark:text-gray-300">
              Required field
            </label>
          </div>
        </div>
      </div>

      {/* Options (for select, radio, checkbox) */}
      {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
        <SortableOptionsPanel
          options={field.options || []}
          onChange={handleOptionsChange}
          onAdd={addOption}
          onUpdate={updateOption}
          onRemove={removeOption}
        />
      )}

      {/* Type-specific Settings */}
      {field.type === 'text' && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Validation</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Min Length</label>
                <input
                  type="number"
                  min="0"
                  value={field.properties.minLength || ''}
                  onChange={(e) => updateField(field.id, {
                    properties: { ...field.properties, minLength: e.target.value ? parseInt(e.target.value) : undefined }
                  })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Max Length</label>
                <input
                  type="number"
                  min="0"
                  value={field.properties.maxLength || ''}
                  onChange={(e) => updateField(field.id, {
                    properties: { ...field.properties, maxLength: e.target.value ? parseInt(e.target.value) : undefined }
                  })}
                  className="input"
                />
              </div>
            </div>
            <div>
              <label className="label">Pattern (Regex)</label>
              <input
                type="text"
                value={field.properties.pattern || ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, pattern: e.target.value || undefined }
                })}
                className="input font-mono text-sm"
                placeholder="e.g., ^[A-Z]+$"
              />
            </div>
          </div>
        </div>
      )}

      {/* Custom Validation Messages */}
      <ValidationMessagesPanel field={field} updateField={updateField} />

      {field.type === 'number' && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Validation</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Minimum</label>
              <input
                type="number"
                value={field.properties.min ?? ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, min: e.target.value ? parseFloat(e.target.value) : undefined }
                })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Maximum</label>
              <input
                type="number"
                value={field.properties.max ?? ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, max: e.target.value ? parseFloat(e.target.value) : undefined }
                })}
                className="input"
              />
            </div>
            <div className="col-span-2">
              <label className="label">Step</label>
              <input
                type="number"
                min="0"
                step="any"
                value={field.properties.step ?? ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, step: e.target.value ? parseFloat(e.target.value) : undefined }
                })}
                className="input"
              />
            </div>
          </div>
        </div>
      )}

      {field.type === 'file' && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">File Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Accepted File Types</label>
              <input
                type="text"
                value={field.properties.accept || ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, accept: e.target.value || undefined }
                })}
                className="input"
                placeholder="e.g., image/*,.pdf"
              />
            </div>
            <div>
              <label className="label">Max File Size (MB)</label>
              <input
                type="number"
                min="1"
                value={(field.properties.maxSize || 10 * 1024 * 1024) / (1024 * 1024)}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, maxSize: parseFloat(e.target.value) * 1024 * 1024 }
                })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Max Files</label>
              <input
                type="number"
                min="1"
                value={field.properties.maxFiles || 1}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, maxFiles: parseInt(e.target.value) }
                })}
                className="input"
              />
            </div>
          </div>
        </div>
      )}

      {field.type === 'select' && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="multiple"
              checked={field.properties.multiple || false}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, multiple: e.target.checked }
              })}
              className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="multiple" className="text-sm text-gray-700 dark:text-gray-300">
              Allow multiple selections
            </label>
          </div>
        </div>
      )}

      {/* Section Settings */}
      {field.type === 'section' && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Section Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Section Description</label>
              <textarea
                value={field.properties.sectionDescription || ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, sectionDescription: e.target.value }
                })}
                className="input"
                rows={2}
                placeholder="Optional description for this section"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="collapsible"
                checked={field.properties.collapsible || false}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, collapsible: e.target.checked }
                })}
                className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="collapsible" className="text-sm text-gray-700 dark:text-gray-300">
                Collapsible section
              </label>
            </div>
            {field.properties.collapsible && (
              <div className="flex items-center gap-2 ml-6">
                <input
                  type="checkbox"
                  id="defaultCollapsed"
                  checked={field.properties.defaultCollapsed || false}
                  onChange={(e) => updateField(field.id, {
                    properties: { ...field.properties, defaultCollapsed: e.target.checked }
                  })}
                  className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="defaultCollapsed" className="text-sm text-gray-700 dark:text-gray-300">
                  Collapsed by default
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Divider Settings */}
      {field.type === 'divider' && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Divider Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Style</label>
              <select
                value={field.properties.dividerStyle || 'solid'}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, dividerStyle: e.target.value as 'solid' | 'dashed' | 'dotted' }
                })}
                className="input"
              >
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
              </select>
            </div>
            <div>
              <label className="label">Color</label>
              <input
                type="color"
                value={field.properties.dividerColor || '#e5e7eb'}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, dividerColor: e.target.value }
                })}
                className="input h-10"
              />
            </div>
          </div>
        </div>
      )}

      {/* Page Break Settings */}
      {field.type === 'page_break' && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Page Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Page Title</label>
              <input
                type="text"
                value={field.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                className="input"
                placeholder="Title for the next page"
              />
            </div>
            <div>
              <label className="label">Page Description</label>
              <textarea
                value={field.properties.pageDescription || ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, pageDescription: e.target.value }
                })}
                className="input"
                rows={2}
                placeholder="Optional description for this page"
              />
            </div>
          </div>
        </div>
      )}

      {/* Rating Settings */}
      {field.type === 'rating' && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Rating Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Maximum Rating</label>
              <input
                type="number"
                min="3"
                max="10"
                value={field.properties.ratingMax || 5}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, ratingMax: parseInt(e.target.value) }
                })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Icon Style</label>
              <select
                value={field.properties.ratingIcon || 'star'}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, ratingIcon: e.target.value as 'star' | 'heart' | 'circle' }
                })}
                className="input"
              >
                <option value="star">Stars</option>
                <option value="heart">Hearts</option>
                <option value="circle">Circles</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Slider Settings */}
      {field.type === 'slider' && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Slider Settings</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Minimum</label>
                <input
                  type="number"
                  value={field.properties.sliderMin ?? 0}
                  onChange={(e) => updateField(field.id, {
                    properties: { ...field.properties, sliderMin: parseFloat(e.target.value) }
                  })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Maximum</label>
                <input
                  type="number"
                  value={field.properties.sliderMax ?? 100}
                  onChange={(e) => updateField(field.id, {
                    properties: { ...field.properties, sliderMax: parseFloat(e.target.value) }
                  })}
                  className="input"
                />
              </div>
            </div>
            <div>
              <label className="label">Step</label>
              <input
                type="number"
                min="0.01"
                step="any"
                value={field.properties.sliderStep ?? 1}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, sliderStep: parseFloat(e.target.value) }
                })}
                className="input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showValue"
                checked={field.properties.showValue !== false}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, showValue: e.target.checked }
                })}
                className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="showValue" className="text-sm text-gray-700 dark:text-gray-300">Show current value</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showMinMax"
                checked={field.properties.showMinMax !== false}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, showMinMax: e.target.checked }
                })}
                className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="showMinMax" className="text-sm text-gray-700 dark:text-gray-300">Show min/max labels</label>
            </div>
          </div>
        </div>
      )}

      {/* Color Picker Settings */}
      {field.type === 'color' && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Color Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Color Format</label>
              <select
                value={field.properties.colorFormat || 'hex'}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, colorFormat: e.target.value as 'hex' | 'rgb' | 'hsl' }
                })}
                className="input"
              >
                <option value="hex">HEX (#RRGGBB)</option>
                <option value="rgb">RGB</option>
                <option value="hsl">HSL</option>
              </select>
            </div>
            <div>
              <label className="label">Preset Colors</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {(field.properties.colorPresets || []).map((color, index) => (
                  <div key={index} className="flex items-center gap-1">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: color }}
                    />
                    <button
                      onClick={() => {
                        const newPresets = [...(field.properties.colorPresets || [])];
                        newPresets.splice(index, 1);
                        updateField(field.id, {
                          properties: { ...field.properties, colorPresets: newPresets }
                        });
                      }}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="color"
                  id="newPresetColor"
                  className="w-8 h-8 rounded border border-gray-300"
                />
                <button
                  onClick={() => {
                    const colorInput = document.getElementById('newPresetColor') as HTMLInputElement;
                    const newColor = colorInput.value;
                    updateField(field.id, {
                      properties: {
                        ...field.properties,
                        colorPresets: [...(field.properties.colorPresets || []), newColor]
                      }
                    });
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  + Add preset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden Field Settings */}
      {field.type === 'hidden' && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Hidden Field Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Hidden Value</label>
              <input
                type="text"
                value={field.properties.hiddenValue || ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, hiddenValue: e.target.value }
                })}
                className="input"
                placeholder="Value to submit"
              />
              <p className="text-xs text-gray-400 mt-1">
                This value will be submitted with the form but not shown to users.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Matrix Settings */}
      {field.type === 'matrix' && (
        <MatrixSettingsPanel field={field} updateField={updateField} />
      )}

      {/* Ranking Settings */}
      {field.type === 'ranking' && (
        <RankingSettingsPanel field={field} updateField={updateField} />
      )}

      {/* Calculated Field Settings */}
      {field.type === 'calculated' && (
        <CalculatedFieldSettingsPanel field={field} allFields={formSchema.fields} updateField={updateField} />
      )}

      {/* Address Field Settings */}
      {field.type === 'address' && (
        <AddressSettingsPanel field={field} updateField={updateField} />
      )}

      {/* Payment Field Settings */}
      {field.type === 'payment' && (
        <PaymentSettingsPanel field={field} updateField={updateField} />
      )}

      {/* Appointment Field Settings */}
      {field.type === 'appointment' && (
        <AppointmentSettingsPanel field={field} updateField={updateField} />
      )}

      {/* NPS Field Settings */}
      {field.type === 'nps' && (
        <NPSSettingsPanel field={field} updateField={updateField} />
      )}

      {/* Country/State Field Settings */}
      {field.type === 'country' && (
        <CountryStateSettingsPanel field={field} updateField={updateField} />
      )}

      {/* Image Choice Field Settings */}
      {field.type === 'image_choice' && (
        <ImageChoiceSettingsPanel field={field} updateField={updateField} />
      )}

      {/* Opinion Scale Field Settings */}
      {field.type === 'opinion_scale' && (
        <OpinionScaleSettingsPanel field={field} updateField={updateField} />
      )}

      {/* Quiz Answer Settings - only show when quiz mode is enabled */}
      {formSchema.settings?.quiz?.enabled && (
        <QuizAnswerSettingsPanel field={field} updateField={updateField} />
      )}

      {/* Conditional Logic Section */}
      {field.type !== 'divider' && (
        <ConditionalLogicPanel
          field={field}
          allFields={formSchema.fields}
          conditionalLogic={formSchema.conditionalLogic}
          onUpdate={(rules) => {
            // Update the store's conditional logic
            const store = useBuilderStore.getState();
            store.updateConditionalLogic(rules);
          }}
        />
      )}
    </div>
  );
}

// Conditional Logic Panel Component
interface ConditionalLogicPanelProps {
  field: FormField;
  allFields: FormField[];
  conditionalLogic: ConditionalRule[];
  onUpdate: (rules: ConditionalRule[]) => void;
}

function ConditionalLogicPanel({ field, allFields, conditionalLogic, onUpdate }: ConditionalLogicPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get rules that target this field
  const fieldRules = conditionalLogic.filter(rule => rule.targetFieldId === field.id);

  // Get fields that can be used as conditions (excluding layout fields)
  const conditionFields = allFields.filter(f =>
    f.id !== field.id &&
    !['section', 'divider', 'page_break'].includes(f.type)
  );

  // Get page breaks for skip_to_page action
  const pageBreaks = allFields.filter(f => f.type === 'page_break');

  const operatorLabels: Record<ConditionOperator, string> = {
    equals: 'equals',
    not_equals: 'does not equal',
    contains: 'contains',
    not_contains: 'does not contain',
    greater_than: 'is greater than',
    less_than: 'is less than',
    is_empty: 'is empty',
    is_not_empty: 'is not empty',
  };

  const actionLabels: Record<ConditionalAction, string> = {
    show: 'Show this field',
    hide: 'Hide this field',
    enable: 'Enable this field',
    disable: 'Disable this field',
    require: 'Make required',
    skip_to_page: 'Skip to page',
  };

  function addRule() {
    if (conditionFields.length === 0) return;

    const newRule: ConditionalRule = {
      id: crypto.randomUUID(),
      targetFieldId: field.id,
      targetType: field.type === 'section' ? 'section' : field.type === 'page_break' ? 'page' : 'field',
      action: 'show',
      conditions: [{
        fieldId: conditionFields[0].id,
        operator: 'equals',
        value: '',
      }],
      operator: 'and',
    };

    onUpdate([...conditionalLogic, newRule]);
  }

  function updateRule(ruleId: string, updates: Partial<ConditionalRule>) {
    onUpdate(conditionalLogic.map(rule =>
      rule.id === ruleId ? { ...rule, ...updates } : rule
    ));
  }

  function removeRule(ruleId: string) {
    onUpdate(conditionalLogic.filter(rule => rule.id !== ruleId));
  }

  function addCondition(ruleId: string) {
    if (conditionFields.length === 0) return;

    onUpdate(conditionalLogic.map(rule => {
      if (rule.id !== ruleId) return rule;
      return {
        ...rule,
        conditions: [...rule.conditions, {
          fieldId: conditionFields[0].id,
          operator: 'equals' as ConditionOperator,
          value: '',
        }],
      };
    }));
  }

  function updateCondition(ruleId: string, conditionIndex: number, updates: Partial<Condition>) {
    onUpdate(conditionalLogic.map(rule => {
      if (rule.id !== ruleId) return rule;
      const newConditions = [...rule.conditions];
      newConditions[conditionIndex] = { ...newConditions[conditionIndex], ...updates };
      return { ...rule, conditions: newConditions };
    }));
  }

  function removeCondition(ruleId: string, conditionIndex: number) {
    onUpdate(conditionalLogic.map(rule => {
      if (rule.id !== ruleId) return rule;
      return {
        ...rule,
        conditions: rule.conditions.filter((_, i) => i !== conditionIndex),
      };
    }));
  }

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full"
      >
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase">Conditional Logic</h3>
        <div className="flex items-center gap-2">
          {fieldRules.length > 0 && (
            <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
              {fieldRules.length} rule{fieldRules.length !== 1 ? 's' : ''}
            </span>
          )}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          {conditionFields.length === 0 ? (
            <p className="text-sm text-gray-500">Add more fields to create conditional logic.</p>
          ) : (
            <>
              {fieldRules.map((rule) => (
                <div key={rule.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-3">
                  {/* Action */}
                  <div className="flex items-center gap-2">
                    <select
                      value={rule.action}
                      onChange={(e) => updateRule(rule.id, { action: e.target.value as ConditionalAction })}
                      className="input text-sm flex-1"
                    >
                      {Object.entries(actionLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeRule(rule.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Skip to page selector */}
                  {rule.action === 'skip_to_page' && pageBreaks.length > 0 && (
                    <select
                      value={rule.skipToPageId || ''}
                      onChange={(e) => updateRule(rule.id, { skipToPageId: e.target.value })}
                      className="input text-sm"
                    >
                      <option value="">Select page...</option>
                      {pageBreaks.map((pb) => (
                        <option key={pb.id} value={pb.id}>{pb.label || 'Untitled Page'}</option>
                      ))}
                    </select>
                  )}

                  {/* Conditions */}
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500">When:</p>
                    {rule.conditions.map((condition, index) => (
                      <div key={index} className="space-y-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                        {index > 0 && (
                          <select
                            value={rule.operator}
                            onChange={(e) => updateRule(rule.id, { operator: e.target.value as 'and' | 'or' })}
                            className="input text-xs w-20"
                          >
                            <option value="and">AND</option>
                            <option value="or">OR</option>
                          </select>
                        )}
                        <div className="flex items-center gap-2">
                          <select
                            value={condition.fieldId}
                            onChange={(e) => updateCondition(rule.id, index, { fieldId: e.target.value })}
                            className="input text-xs min-w-0 flex-1"
                          >
                            {conditionFields.map((f) => (
                              <option key={f.id} value={f.id}>{f.label}</option>
                            ))}
                          </select>
                          {rule.conditions.length > 1 && (
                            <button
                              onClick={() => removeCondition(rule.id, index)}
                              className="p-1 text-gray-400 hover:text-red-600 flex-shrink-0"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={condition.operator}
                            onChange={(e) => updateCondition(rule.id, index, { operator: e.target.value as ConditionOperator })}
                            className="input text-xs flex-1"
                          >
                            {Object.entries(operatorLabels).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>
                          {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                            <input
                              type="text"
                              value={String(condition.value || '')}
                              onChange={(e) => updateCondition(rule.id, index, { value: e.target.value })}
                              className="input text-xs flex-1"
                              placeholder="Value"
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => addCondition(rule.id)}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    + Add condition
                  </button>
                </div>
              ))}

              <button
                onClick={addRule}
                className="w-full py-2 text-sm text-primary-600 hover:text-primary-700 border border-dashed border-primary-300 rounded-lg hover:border-primary-400"
              >
                + Add conditional rule
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Validation Messages Panel Component
interface ValidationMessagesPanelProps {
  field: FormField;
  updateField: (fieldId: string, updates: Partial<FormField>) => void;
}

function ValidationMessagesPanel({ field, updateField }: ValidationMessagesPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Skip layout fields
  if (['section', 'divider', 'page_break'].includes(field.type)) {
    return null;
  }

  const messages = field.properties.validationMessages || {};

  function updateMessage(key: keyof ValidationMessages, value: string) {
    updateField(field.id, {
      properties: {
        ...field.properties,
        validationMessages: {
          ...messages,
          [key]: value || undefined,
        },
      },
    });
  }

  // Determine which validation message fields to show based on field type and settings
  const showRequired = true; // All fields can be required
  const showMinLength = field.type === 'text' || field.type === 'textarea';
  const showMaxLength = field.type === 'text' || field.type === 'textarea';
  const showMin = field.type === 'number';
  const showMax = field.type === 'number';
  const showPattern = field.type === 'text' && field.properties.pattern;
  const showEmail = field.type === 'email';

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full"
      >
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase">Error Messages</h3>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-gray-500 mb-3">
            Customize error messages shown when validation fails. Leave blank to use defaults.
          </p>

          {showRequired && (
            <div>
              <label className="label">Required Field Message</label>
              <input
                type="text"
                value={messages.required || ''}
                onChange={(e) => updateMessage('required', e.target.value)}
                className="input text-sm"
                placeholder={`${field.label} is required`}
              />
            </div>
          )}

          {showMinLength && field.properties.minLength && (
            <div>
              <label className="label">Min Length Message</label>
              <input
                type="text"
                value={messages.minLength || ''}
                onChange={(e) => updateMessage('minLength', e.target.value)}
                className="input text-sm"
                placeholder={`Must be at least ${field.properties.minLength} characters`}
              />
            </div>
          )}

          {showMaxLength && field.properties.maxLength && (
            <div>
              <label className="label">Max Length Message</label>
              <input
                type="text"
                value={messages.maxLength || ''}
                onChange={(e) => updateMessage('maxLength', e.target.value)}
                className="input text-sm"
                placeholder={`Must be no more than ${field.properties.maxLength} characters`}
              />
            </div>
          )}

          {showMin && field.properties.min !== undefined && (
            <div>
              <label className="label">Minimum Value Message</label>
              <input
                type="text"
                value={messages.min || ''}
                onChange={(e) => updateMessage('min', e.target.value)}
                className="input text-sm"
                placeholder={`Must be at least ${field.properties.min}`}
              />
            </div>
          )}

          {showMax && field.properties.max !== undefined && (
            <div>
              <label className="label">Maximum Value Message</label>
              <input
                type="text"
                value={messages.max || ''}
                onChange={(e) => updateMessage('max', e.target.value)}
                className="input text-sm"
                placeholder={`Must be no more than ${field.properties.max}`}
              />
            </div>
          )}

          {showPattern && (
            <div>
              <label className="label">Pattern Mismatch Message</label>
              <input
                type="text"
                value={messages.pattern || ''}
                onChange={(e) => updateMessage('pattern', e.target.value)}
                className="input text-sm"
                placeholder="Invalid format"
              />
            </div>
          )}

          {showEmail && (
            <div>
              <label className="label">Invalid Email Message</label>
              <input
                type="text"
                value={messages.email || ''}
                onChange={(e) => updateMessage('email', e.target.value)}
                className="input text-sm"
                placeholder="Please enter a valid email address"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Matrix Settings Panel Component
interface MatrixSettingsPanelProps {
  field: FormField;
  updateField: (fieldId: string, updates: Partial<FormField>) => void;
}

function MatrixSettingsPanel({ field, updateField }: MatrixSettingsPanelProps) {
  const rows = field.properties.matrixRows || [];
  const columns = field.properties.matrixColumns || [];

  function addRow() {
    const newRow: MatrixRow = {
      id: `row_${crypto.randomUUID().slice(0, 8)}`,
      label: `Row ${rows.length + 1}`,
    };
    updateField(field.id, {
      properties: { ...field.properties, matrixRows: [...rows, newRow] },
    });
  }

  function updateRow(index: number, label: string) {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], label };
    updateField(field.id, {
      properties: { ...field.properties, matrixRows: newRows },
    });
  }

  function removeRow(index: number) {
    updateField(field.id, {
      properties: { ...field.properties, matrixRows: rows.filter((_, i) => i !== index) },
    });
  }

  function addColumn() {
    const newColumn: MatrixColumn = {
      id: `col_${crypto.randomUUID().slice(0, 8)}`,
      label: `Option ${columns.length + 1}`,
      value: `option${columns.length + 1}`,
    };
    updateField(field.id, {
      properties: { ...field.properties, matrixColumns: [...columns, newColumn] },
    });
  }

  function updateColumn(index: number, label: string) {
    const newColumns = [...columns];
    newColumns[index] = { ...newColumns[index], label, value: label.toLowerCase().replace(/\s+/g, '_') };
    updateField(field.id, {
      properties: { ...field.properties, matrixColumns: newColumns },
    });
  }

  function removeColumn(index: number) {
    updateField(field.id, {
      properties: { ...field.properties, matrixColumns: columns.filter((_, i) => i !== index) },
    });
  }

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Matrix Settings</h3>
      <div className="space-y-4">
        {/* Selection Type */}
        <div>
          <label className="label">Selection Type</label>
          <select
            value={field.properties.matrixType || 'single'}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, matrixType: e.target.value as 'single' | 'multiple' }
            })}
            className="input"
          >
            <option value="single">Single selection per row</option>
            <option value="multiple">Multiple selections per row</option>
          </select>
        </div>

        {/* Rows */}
        <div>
          <label className="label">Rows (Questions)</label>
          <div className="space-y-2 mt-2">
            {rows.map((row, index) => (
              <div key={row.id} className="flex items-center gap-2">
                <input
                  type="text"
                  value={row.label}
                  onChange={(e) => updateRow(index, e.target.value)}
                  className="input flex-1 text-sm"
                  placeholder="Row label"
                />
                <button
                  onClick={() => removeRow(index)}
                  disabled={rows.length <= 1}
                  className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={addRow}
            className="mt-2 text-sm text-primary-600 hover:text-primary-700"
          >
            + Add row
          </button>
        </div>

        {/* Columns */}
        <div>
          <label className="label">Columns (Options)</label>
          <div className="space-y-2 mt-2">
            {columns.map((col, index) => (
              <div key={col.id} className="flex items-center gap-2">
                <input
                  type="text"
                  value={col.label}
                  onChange={(e) => updateColumn(index, e.target.value)}
                  className="input flex-1 text-sm"
                  placeholder="Column label"
                />
                <button
                  onClick={() => removeColumn(index)}
                  disabled={columns.length <= 2}
                  className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={addColumn}
            className="mt-2 text-sm text-primary-600 hover:text-primary-700"
          >
            + Add column
          </button>
        </div>
      </div>
    </div>
  );
}

// Ranking Settings Panel Component
interface RankingSettingsPanelProps {
  field: FormField;
  updateField: (fieldId: string, updates: Partial<FormField>) => void;
}

function RankingSettingsPanel({ field, updateField }: RankingSettingsPanelProps) {
  const items = field.properties.rankingItems || [];

  function addItem() {
    const newItem: RankingItem = {
      id: `item_${crypto.randomUUID().slice(0, 8)}`,
      label: `Item ${items.length + 1}`,
    };
    updateField(field.id, {
      properties: { ...field.properties, rankingItems: [...items, newItem] },
    });
  }

  function updateItem(index: number, label: string) {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], label };
    updateField(field.id, {
      properties: { ...field.properties, rankingItems: newItems },
    });
  }

  function removeItem(index: number) {
    updateField(field.id, {
      properties: { ...field.properties, rankingItems: items.filter((_, i) => i !== index) },
    });
  }

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Ranking Items</h3>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={item.id} className="flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded text-xs font-medium text-gray-600">
              {index + 1}
            </span>
            <input
              type="text"
              value={item.label}
              onChange={(e) => updateItem(index, e.target.value)}
              className="input flex-1 text-sm"
              placeholder="Item label"
            />
            <button
              onClick={() => removeItem(index)}
              disabled={items.length <= 2}
              className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={addItem}
        className="mt-3 text-sm text-primary-600 hover:text-primary-700"
      >
        + Add item
      </button>
      <p className="text-xs text-gray-400 mt-2">
        Users will drag items to rank them in their preferred order.
      </p>
    </div>
  );
}

// Calculated Field Settings Panel
interface CalculatedFieldSettingsPanelProps {
  field: FormField;
  allFields: FormField[];
  updateField: (id: string, updates: Partial<FormField>) => void;
}

function CalculatedFieldSettingsPanel({ field, allFields, updateField }: CalculatedFieldSettingsPanelProps) {
  const calculationType = field.properties.calculationType || 'sum';
  const selectedFields = field.properties.calculationFields || [];
  const formula = field.properties.calculationFormula || '';

  // Get numeric fields that can be used in calculations
  const numericFields = allFields.filter(f =>
    f.id !== field.id &&
    ['number', 'rating', 'slider'].includes(f.type)
  );

  function toggleField(fieldId: string) {
    const newSelected = selectedFields.includes(fieldId)
      ? selectedFields.filter(id => id !== fieldId)
      : [...selectedFields, fieldId];
    updateField(field.id, {
      properties: { ...field.properties, calculationFields: newSelected }
    });
  }

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Calculation Settings</h3>

      {/* Calculation Type */}
      <div className="mb-4">
        <label className="label">Calculation Type</label>
        <select
          value={calculationType}
          onChange={(e) => updateField(field.id, {
            properties: { ...field.properties, calculationType: e.target.value as 'sum' | 'average' | 'min' | 'max' | 'count' | 'formula' }
          })}
          className="input"
        >
          <option value="sum">Sum</option>
          <option value="average">Average</option>
          <option value="min">Minimum</option>
          <option value="max">Maximum</option>
          <option value="count">Count</option>
          <option value="formula">Custom Formula</option>
        </select>
      </div>

      {/* Field Selection (for built-in operations) */}
      {calculationType !== 'formula' && (
        <div className="mb-4">
          <label className="label">Fields to Calculate</label>
          {numericFields.length === 0 ? (
            <p className="text-sm text-gray-500">Add number, rating, or slider fields first.</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
              {numericFields.map((f) => (
                <label key={f.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedFields.includes(f.id)}
                    onChange={() => toggleField(f.id)}
                    className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-primary-600"
                  />
                  <span className="text-sm">{f.label}</span>
                  <span className="text-xs text-gray-400">({f.type})</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Custom Formula */}
      {calculationType === 'formula' && (
        <div className="mb-4">
          <label className="label">Formula</label>
          <textarea
            value={formula}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, calculationFormula: e.target.value }
            })}
            placeholder="{field_name} + {other_field} * 2"
            className="input min-h-[80px]"
          />
          <p className="text-xs text-gray-500 mt-1">
            Use {'{field_name}'} to reference other fields. Supports: + - * / ( ) abs() round() min() max()
          </p>
          {numericFields.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-600 mb-1">Available fields:</p>
              <div className="flex flex-wrap gap-1">
                {numericFields.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => updateField(field.id, {
                      properties: { ...field.properties, calculationFormula: formula + `{${f.name}}` }
                    })}
                    className="px-2 py-0.5 text-xs bg-gray-100 rounded hover:bg-gray-200"
                  >
                    {'{' + f.name + '}'}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Format Options */}
      <div className="mb-4">
        <label className="label">Display Format</label>
        <select
          value={field.properties.calculationFormat || 'number'}
          onChange={(e) => updateField(field.id, {
            properties: { ...field.properties, calculationFormat: e.target.value as 'number' | 'currency' | 'percent' }
          })}
          className="input"
        >
          <option value="number">Number</option>
          <option value="currency">Currency</option>
          <option value="percent">Percentage</option>
        </select>
      </div>

      {/* Currency Symbol (for currency format) */}
      {field.properties.calculationFormat === 'currency' && (
        <div className="mb-4">
          <label className="label">Currency Symbol</label>
          <input
            type="text"
            value={field.properties.calculationCurrency || '$'}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, calculationCurrency: e.target.value }
            })}
            className="input"
            placeholder="$"
          />
        </div>
      )}

      {/* Decimal Places */}
      <div className="mb-4">
        <label className="label">Decimal Places</label>
        <input
          type="number"
          min={0}
          max={10}
          value={field.properties.calculationDecimals ?? 2}
          onChange={(e) => updateField(field.id, {
            properties: { ...field.properties, calculationDecimals: parseInt(e.target.value) || 0 }
          })}
          className="input"
        />
      </div>

      {/* Prefix/Suffix */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div>
          <label className="label">Prefix</label>
          <input
            type="text"
            value={field.properties.calculationPrefix || ''}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, calculationPrefix: e.target.value }
            })}
            className="input"
            placeholder="Total: "
          />
        </div>
        <div>
          <label className="label">Suffix</label>
          <input
            type="text"
            value={field.properties.calculationSuffix || ''}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, calculationSuffix: e.target.value }
            })}
            className="input"
            placeholder=" USD"
          />
        </div>
      </div>
    </div>
  );
}

// Quiz Answer Settings Panel
interface QuizAnswerSettingsPanelProps {
  field: FormField;
  updateField: (id: string, updates: Partial<FormField>) => void;
}

function QuizAnswerSettingsPanel({ field, updateField }: QuizAnswerSettingsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Skip non-scorable fields
  if (['section', 'divider', 'page_break', 'calculated', 'file', 'signature', 'hidden'].includes(field.type)) {
    return null;
  }

  const correctAnswer = field.properties.correctAnswer;
  const points = field.properties.points ?? 1;

  // Get the appropriate input type based on field type
  const renderAnswerInput = () => {
    switch (field.type) {
      case 'text':
      case 'textarea':
      case 'email':
      case 'url':
        return (
          <div className="space-y-2">
            <input
              type="text"
              value={String(correctAnswer || '')}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, correctAnswer: e.target.value }
              })}
              className="input"
              placeholder="Correct answer"
            />
            <p className="text-xs text-gray-500">
              For multiple acceptable answers, separate with | (e.g., "answer1|answer2")
            </p>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={field.properties.caseSensitive || false}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, caseSensitive: e.target.checked }
                })}
                className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Case sensitive</span>
            </label>
          </div>
        );

      case 'number':
      case 'slider':
      case 'rating':
        return (
          <input
            type="number"
            value={correctAnswer as number || ''}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, correctAnswer: Number(e.target.value) }
            })}
            className="input"
            placeholder="Correct number"
          />
        );

      case 'select':
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`correct_${field.id}`}
                  checked={correctAnswer === opt.value}
                  onChange={() => updateField(field.id, {
                    properties: { ...field.properties, correctAnswer: opt.value }
                  })}
                  className="border-gray-300 text-blue-600"
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options?.map((opt) => {
              const correctAnswers = Array.isArray(correctAnswer) ? correctAnswer : [];
              const isChecked = correctAnswers.includes(opt.value);
              return (
                <label key={opt.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => {
                      const newAnswers = isChecked
                        ? correctAnswers.filter(v => v !== opt.value)
                        : [...correctAnswers, opt.value];
                      updateField(field.id, {
                        properties: { ...field.properties, correctAnswer: newAnswers }
                      });
                    }}
                    className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600"
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              );
            })}
            <label className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={field.properties.partialCredit || false}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, partialCredit: e.target.checked }
                })}
                className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Allow partial credit</span>
            </label>
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            value={correctAnswer as string || ''}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, correctAnswer: e.target.value }
            })}
            className="input"
          />
        );

      default:
        return (
          <input
            type="text"
            value={String(correctAnswer || '')}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, correctAnswer: e.target.value }
            })}
            className="input"
            placeholder="Correct answer"
          />
        );
    }
  };

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm">🎓</span>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase">Quiz Settings</h3>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <p className="text-xs text-gray-500">
            Set the correct answer and point value for this question in quiz mode.
          </p>

          {/* Points */}
          <div>
            <label className="label">Points</label>
            <input
              type="number"
              min={0}
              value={points}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, points: Number(e.target.value) || 1 }
              })}
              className="input w-24"
            />
          </div>

          {/* Correct Answer */}
          <div>
            <label className="label">Correct Answer</label>
            {renderAnswerInput()}
          </div>

          {/* Explanation */}
          <div>
            <label className="label">Explanation (optional)</label>
            <textarea
              value={field.properties.explanation || ''}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, explanation: e.target.value }
              })}
              className="input min-h-[60px]"
              placeholder="Explain why this is the correct answer..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Shown after submission if correct answers are displayed.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// Address Settings Panel
interface AddressSettingsPanelProps {
  field: FormField;
  updateField: (id: string, updates: Partial<FormField>) => void;
}

type AddressComponent = 'street' | 'street2' | 'city' | 'state' | 'zip' | 'country';

function AddressSettingsPanel({ field, updateField }: AddressSettingsPanelProps) {
  const components: AddressComponent[] = (field.properties.addressComponents || ['street', 'city', 'state', 'zip', 'country']) as AddressComponent[];

  const allComponents: { id: AddressComponent; label: string }[] = [
    { id: 'street', label: 'Street Address' },
    { id: 'street2', label: 'Address Line 2' },
    { id: 'city', label: 'City' },
    { id: 'state', label: 'State/Province' },
    { id: 'zip', label: 'ZIP/Postal Code' },
    { id: 'country', label: 'Country' },
  ];

  const toggleComponent = (componentId: AddressComponent) => {
    const newComponents = components.includes(componentId)
      ? components.filter(c => c !== componentId)
      : [...components, componentId];
    updateField(field.id, {
      properties: { ...field.properties, addressComponents: newComponents }
    });
  };

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Address Settings</h3>
      <div className="space-y-4">
        <div>
          <label className="label">Address Components</label>
          <div className="space-y-2 mt-2">
            {allComponents.map((comp) => (
              <label key={comp.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={components.includes(comp.id)}
                  onChange={() => toggleComponent(comp.id)}
                  className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-primary-600"
                />
                <span className="text-sm">{comp.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="label">Default Country</label>
          <input
            type="text"
            value={field.properties.defaultCountry || ''}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, defaultCountry: e.target.value }
            })}
            className="input"
            placeholder="e.g., US"
          />
        </div>

        <div>
          <label className="label">Google Places API Key (optional)</label>
          <input
            type="text"
            value={field.properties.googlePlacesApiKey || ''}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, googlePlacesApiKey: e.target.value }
            })}
            className="input"
            placeholder="For autocomplete feature"
          />
          <p className="text-xs text-gray-400 mt-1">
            Enable address autocomplete with a Google Places API key
          </p>
        </div>
      </div>
    </div>
  );
}

// Payment Settings Panel
interface PaymentSettingsPanelProps {
  field: FormField;
  updateField: (id: string, updates: Partial<FormField>) => void;
}

function PaymentSettingsPanel({ field, updateField }: PaymentSettingsPanelProps) {
  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Payment Settings</h3>
      <div className="space-y-4">
        <div>
          <label className="label">Payment Provider</label>
          <select
            value={field.properties.paymentProvider || 'stripe'}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, paymentProvider: e.target.value as 'stripe' | 'paypal' }
            })}
            className="input"
          >
            <option value="stripe">Stripe</option>
            <option value="paypal">PayPal</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={field.properties.paymentAmount || ''}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, paymentAmount: parseFloat(e.target.value) || undefined }
              })}
              className="input"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="label">Currency</label>
            <select
              value={field.properties.paymentCurrency || 'USD'}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, paymentCurrency: e.target.value }
              })}
              className="input"
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="ILS">ILS (₪)</option>
              <option value="CAD">CAD ($)</option>
              <option value="AUD">AUD ($)</option>
            </select>
          </div>
        </div>

        <div>
          <label className="label">Payment Description</label>
          <input
            type="text"
            value={field.properties.paymentDescription || ''}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, paymentDescription: e.target.value }
            })}
            className="input"
            placeholder="What the payment is for"
          />
        </div>

        <div>
          <label className="label">Mode</label>
          <select
            value={field.properties.paymentMode || 'test'}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, paymentMode: e.target.value as 'live' | 'test' }
            })}
            className="input"
          >
            <option value="test">Test Mode</option>
            <option value="live">Live Mode</option>
          </select>
        </div>

        {field.properties.paymentProvider === 'stripe' && (
          <div>
            <label className="label">Stripe Public Key</label>
            <input
              type="text"
              value={field.properties.stripePublicKey || ''}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, stripePublicKey: e.target.value }
              })}
              className="input"
              placeholder="pk_test_..."
            />
          </div>
        )}

        {field.properties.paymentProvider === 'paypal' && (
          <div>
            <label className="label">PayPal Client ID</label>
            <input
              type="text"
              value={field.properties.paypalClientId || ''}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, paypalClientId: e.target.value }
              })}
              className="input"
              placeholder="Your PayPal client ID"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Appointment Settings Panel
interface AppointmentSettingsPanelProps {
  field: FormField;
  updateField: (id: string, updates: Partial<FormField>) => void;
}

function AppointmentSettingsPanel({ field, updateField }: AppointmentSettingsPanelProps) {
  const availability = field.properties.appointmentAvailability || [];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const toggleDay = (dayOfWeek: number) => {
    const existing = availability.find(a => a.dayOfWeek === dayOfWeek);
    if (existing) {
      updateField(field.id, {
        properties: {
          ...field.properties,
          appointmentAvailability: availability.filter(a => a.dayOfWeek !== dayOfWeek)
        }
      });
    } else {
      const newAvailability: AppointmentAvailability = {
        dayOfWeek,
        slots: [{ start: '09:00', end: '17:00' }]
      };
      updateField(field.id, {
        properties: {
          ...field.properties,
          appointmentAvailability: [...availability, newAvailability]
        }
      });
    }
  };

  const updateSlot = (dayOfWeek: number, start: string, end: string) => {
    updateField(field.id, {
      properties: {
        ...field.properties,
        appointmentAvailability: availability.map(a =>
          a.dayOfWeek === dayOfWeek ? { ...a, slots: [{ start, end }] } : a
        )
      }
    });
  };

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Appointment Settings</h3>
      <div className="space-y-4">
        <div>
          <label className="label">Appointment Duration (minutes)</label>
          <input
            type="number"
            min="5"
            step="5"
            value={field.properties.appointmentDuration || 30}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, appointmentDuration: parseInt(e.target.value) }
            })}
            className="input"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Min Advance (hours)</label>
            <input
              type="number"
              min="0"
              value={field.properties.appointmentMinAdvance || 24}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, appointmentMinAdvance: parseInt(e.target.value) }
              })}
              className="input"
            />
          </div>
          <div>
            <label className="label">Max Advance (days)</label>
            <input
              type="number"
              min="1"
              value={field.properties.appointmentMaxAdvance || 30}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, appointmentMaxAdvance: parseInt(e.target.value) }
              })}
              className="input"
            />
          </div>
        </div>

        <div>
          <label className="label">Availability</label>
          <div className="space-y-2 mt-2">
            {dayNames.map((day, index) => {
              const dayAvailability = availability.find(a => a.dayOfWeek === index);
              return (
                <div key={day} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!dayAvailability}
                    onChange={() => toggleDay(index)}
                    className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-primary-600"
                  />
                  <span className="text-sm w-20">{day}</span>
                  {dayAvailability && (
                    <>
                      <input
                        type="time"
                        value={dayAvailability.slots[0]?.start || '09:00'}
                        onChange={(e) => updateSlot(index, e.target.value, dayAvailability.slots[0]?.end || '17:00')}
                        className="input text-sm py-1 px-2"
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={dayAvailability.slots[0]?.end || '17:00'}
                        onChange={(e) => updateSlot(index, dayAvailability.slots[0]?.start || '09:00', e.target.value)}
                        className="input text-sm py-1 px-2"
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <label className="label">Timezone</label>
          <select
            value={field.properties.appointmentTimezone || 'America/New_York'}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, appointmentTimezone: e.target.value }
            })}
            className="input"
          >
            <option value="America/New_York">Eastern Time (US)</option>
            <option value="America/Chicago">Central Time (US)</option>
            <option value="America/Denver">Mountain Time (US)</option>
            <option value="America/Los_Angeles">Pacific Time (US)</option>
            <option value="Europe/London">London (UK)</option>
            <option value="Europe/Paris">Paris (EU)</option>
            <option value="Asia/Jerusalem">Jerusalem (Israel)</option>
            <option value="Asia/Tokyo">Tokyo (Japan)</option>
          </select>
        </div>
      </div>
    </div>
  );
}

// NPS Settings Panel
interface NPSSettingsPanelProps {
  field: FormField;
  updateField: (id: string, updates: Partial<FormField>) => void;
}

function NPSSettingsPanel({ field, updateField }: NPSSettingsPanelProps) {
  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">NPS Settings</h3>
      <div className="space-y-4">
        <div>
          <label className="label">Left Label (0)</label>
          <input
            type="text"
            value={field.properties.npsLeftLabel || 'Not likely at all'}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, npsLeftLabel: e.target.value }
            })}
            className="input"
            placeholder="Not likely at all"
          />
        </div>

        <div>
          <label className="label">Right Label (10)</label>
          <input
            type="text"
            value={field.properties.npsRightLabel || 'Extremely likely'}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, npsRightLabel: e.target.value }
            })}
            className="input"
            placeholder="Extremely likely"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="npsShowLabels"
            checked={field.properties.npsShowLabels !== false}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, npsShowLabels: e.target.checked }
            })}
            className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="npsShowLabels" className="text-sm text-gray-700 dark:text-gray-300">
            Show labels under numbers
          </label>
        </div>

        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500">
            <span className="font-medium">NPS Categories:</span><br />
            0-6 = Detractors (red)<br />
            7-8 = Passives (yellow)<br />
            9-10 = Promoters (green)
          </p>
        </div>
      </div>
    </div>
  );
}

// Country/State Settings Panel
interface CountryStateSettingsPanelProps {
  field: FormField;
  updateField: (id: string, updates: Partial<FormField>) => void;
}

function CountryStateSettingsPanel({ field, updateField }: CountryStateSettingsPanelProps) {
  const popularCountries = field.properties.popularCountries || ['US', 'GB', 'CA', 'AU'];
  const [newCountry, setNewCountry] = useState('');

  const addPopularCountry = () => {
    if (newCountry.trim() && !popularCountries.includes(newCountry.toUpperCase())) {
      updateField(field.id, {
        properties: {
          ...field.properties,
          popularCountries: [...popularCountries, newCountry.toUpperCase()]
        }
      });
      setNewCountry('');
    }
  };

  const removePopularCountry = (code: string) => {
    updateField(field.id, {
      properties: {
        ...field.properties,
        popularCountries: popularCountries.filter(c => c !== code)
      }
    });
  };

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Country/State Settings</h3>
      <div className="space-y-4">
        <div>
          <label className="label">Field Type</label>
          <select
            value={field.properties.countryFieldType || 'country_state'}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, countryFieldType: e.target.value as 'country_only' | 'country_state' | 'country_state_city' }
            })}
            className="input"
          >
            <option value="country_only">Country Only</option>
            <option value="country_state">Country + State/Province</option>
            <option value="country_state_city">Country + State + City</option>
          </select>
        </div>

        <div>
          <label className="label">Default Country Code</label>
          <input
            type="text"
            value={field.properties.defaultCountryCode || ''}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, defaultCountryCode: e.target.value.toUpperCase() }
            })}
            className="input"
            placeholder="e.g., US, GB, IL"
            maxLength={2}
          />
        </div>

        <div>
          <label className="label">Popular Countries (shown first)</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {popularCountries.map((code) => (
              <span key={code} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm">
                {code}
                <button
                  onClick={() => removePopularCountry(code)}
                  className="text-gray-400 hover:text-red-600"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <input
              type="text"
              value={newCountry}
              onChange={(e) => setNewCountry(e.target.value.toUpperCase())}
              className="input flex-1"
              placeholder="Country code"
              maxLength={2}
            />
            <button
              onClick={addPopularCountry}
              disabled={!newCountry.trim()}
              className="px-3 py-2 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Image Choice Settings Panel
interface ImageChoiceSettingsPanelProps {
  field: FormField;
  updateField: (id: string, updates: Partial<FormField>) => void;
}

function ImageChoiceSettingsPanel({ field, updateField }: ImageChoiceSettingsPanelProps) {
  const choices = field.properties.imageChoices || [];

  const addChoice = () => {
    const newChoice: ImageChoiceOption = {
      id: `choice_${crypto.randomUUID().slice(0, 8)}`,
      label: `Choice ${choices.length + 1}`,
      value: `choice${choices.length + 1}`,
      imageUrl: '',
    };
    updateField(field.id, {
      properties: { ...field.properties, imageChoices: [...choices, newChoice] }
    });
  };

  const updateChoice = (index: number, updates: Partial<ImageChoiceOption>) => {
    const newChoices = [...choices];
    newChoices[index] = { ...newChoices[index], ...updates };
    updateField(field.id, {
      properties: { ...field.properties, imageChoices: newChoices }
    });
  };

  const removeChoice = (index: number) => {
    updateField(field.id, {
      properties: { ...field.properties, imageChoices: choices.filter((_, i) => i !== index) }
    });
  };

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Image Choice Settings</h3>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Columns</label>
            <select
              value={field.properties.imageChoiceColumns || 3}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, imageChoiceColumns: parseInt(e.target.value) as 2 | 3 | 4 }
              })}
              className="input"
            >
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
              <option value={4}>4 Columns</option>
            </select>
          </div>
          <div className="flex flex-col justify-end">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="imageChoiceMultiple"
                checked={field.properties.imageChoiceMultiple || false}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, imageChoiceMultiple: e.target.checked }
                })}
                className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-primary-600"
              />
              <label htmlFor="imageChoiceMultiple" className="text-sm text-gray-700 dark:text-gray-300">Allow multiple</label>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="imageChoiceShowLabels"
            checked={field.properties.imageChoiceShowLabels !== false}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, imageChoiceShowLabels: e.target.checked }
            })}
            className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-primary-600"
          />
          <label htmlFor="imageChoiceShowLabels" className="text-sm text-gray-700 dark:text-gray-300">Show labels under images</label>
        </div>

        <div>
          <label className="label">Choices</label>
          <div className="space-y-3 mt-2">
            {choices.map((choice, index) => (
              <div key={choice.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500">Choice {index + 1}</span>
                  <button
                    onClick={() => removeChoice(index)}
                    disabled={choices.length <= 1}
                    className="text-gray-400 hover:text-red-600 disabled:opacity-30"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <input
                  type="text"
                  value={choice.label}
                  onChange={(e) => updateChoice(index, { label: e.target.value, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  className="input text-sm"
                  placeholder="Label"
                />
                <input
                  type="url"
                  value={choice.imageUrl || ''}
                  onChange={(e) => updateChoice(index, { imageUrl: e.target.value })}
                  className="input text-sm"
                  placeholder="Image URL"
                />
                <input
                  type="text"
                  value={choice.description || ''}
                  onChange={(e) => updateChoice(index, { description: e.target.value })}
                  className="input text-sm"
                  placeholder="Description (optional)"
                />
              </div>
            ))}
          </div>
          <button
            onClick={addChoice}
            className="mt-3 text-sm text-primary-600 hover:text-primary-700"
          >
            + Add choice
          </button>
        </div>
      </div>
    </div>
  );
}

// Opinion Scale Settings Panel
interface OpinionScaleSettingsPanelProps {
  field: FormField;
  updateField: (id: string, updates: Partial<FormField>) => void;
}

function OpinionScaleSettingsPanel({ field, updateField }: OpinionScaleSettingsPanelProps) {
  const min = field.properties.opinionScaleMin ?? 1;
  const max = field.properties.opinionScaleMax ?? 5;
  const labels = field.properties.opinionScaleLabels || [];

  const updateLabel = (value: number, label: string) => {
    const existing = labels.find(l => l.value === value);
    let newLabels: OpinionScaleLabel[];
    if (existing) {
      newLabels = labels.map(l => l.value === value ? { ...l, label } : l);
    } else {
      newLabels = [...labels, { value, label }];
    }
    updateField(field.id, {
      properties: { ...field.properties, opinionScaleLabels: newLabels }
    });
  };

  const getLabel = (value: number) => {
    return labels.find(l => l.value === value)?.label || '';
  };

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Opinion Scale Settings</h3>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Minimum Value</label>
            <input
              type="number"
              min={0}
              max={max - 1}
              value={min}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, opinionScaleMin: parseInt(e.target.value) }
              })}
              className="input"
            />
          </div>
          <div>
            <label className="label">Maximum Value</label>
            <input
              type="number"
              min={min + 1}
              max={10}
              value={max}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, opinionScaleMax: parseInt(e.target.value) }
              })}
              className="input"
            />
          </div>
        </div>

        <div>
          <label className="label">Left Endpoint Label</label>
          <input
            type="text"
            value={field.properties.opinionScaleLeftLabel || ''}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, opinionScaleLeftLabel: e.target.value }
            })}
            className="input"
            placeholder="e.g., Strongly Disagree"
          />
        </div>

        <div>
          <label className="label">Right Endpoint Label</label>
          <input
            type="text"
            value={field.properties.opinionScaleRightLabel || ''}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, opinionScaleRightLabel: e.target.value }
            })}
            className="input"
            placeholder="e.g., Strongly Agree"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="opinionScaleShowNumbers"
            checked={field.properties.opinionScaleShowNumbers !== false}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, opinionScaleShowNumbers: e.target.checked }
            })}
            className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-primary-600"
          />
          <label htmlFor="opinionScaleShowNumbers" className="text-sm text-gray-700 dark:text-gray-300">
            Show numbers on scale
          </label>
        </div>

        <div>
          <label className="label">Custom Labels (optional)</label>
          <p className="text-xs text-gray-500 mb-2">Add labels below individual scale points</p>
          <div className="space-y-2">
            {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((value) => (
              <div key={value} className="flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded text-sm font-medium">
                  {value}
                </span>
                <input
                  type="text"
                  value={getLabel(value)}
                  onChange={(e) => updateLabel(value, e.target.value)}
                  className="input flex-1 text-sm"
                  placeholder="Optional label"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Sortable Options Panel for select/radio/checkbox fields
interface SortableOptionsPanelProps {
  options: FieldOption[];
  onChange: (options: FieldOption[]) => void;
  onAdd: () => void;
  onUpdate: (index: number, updates: Partial<FieldOption>) => void;
  onRemove: (index: number) => void;
}

function SortableOptionsPanel({ options, onChange, onAdd, onUpdate, onRemove }: SortableOptionsPanelProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = options.findIndex((_, i) => `option-${i}` === active.id);
      const newIndex = options.findIndex((_, i) => `option-${i}` === over.id);
      onChange(arrayMove(options, oldIndex, newIndex));
    }
  };

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase">Options</h3>
        <span className="text-xs text-gray-400">Drag to reorder</span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={options.map((_, i) => `option-${i}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {options.map((option, index) => (
              <SortableOptionItem
                key={`option-${index}`}
                id={`option-${index}`}
                option={option}
                index={index}
                onUpdate={onUpdate}
                onRemove={onRemove}
                canRemove={options.length > 1}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button
        onClick={onAdd}
        className="mt-3 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add Option
      </button>
    </div>
  );
}

// Sortable Option Item
interface SortableOptionItemProps {
  id: string;
  option: FieldOption;
  index: number;
  onUpdate: (index: number, updates: Partial<FieldOption>) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

function SortableOptionItem({ id, option, index, onUpdate, onRemove, canRemove }: SortableOptionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 ${isDragging ? 'z-50' : ''}`}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
        title="Drag to reorder"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
        </svg>
      </button>

      {/* Option Input */}
      <input
        type="text"
        value={option.label}
        onChange={(e) => onUpdate(index, { label: e.target.value, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
        className="input flex-1"
        placeholder="Option label"
      />

      {/* Remove Button */}
      <button
        onClick={() => onRemove(index)}
        className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
        disabled={!canRemove}
        title="Remove option"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
